import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  QUESTION_TYPE_LABELS, YN_TYPES, YN_CODES, YN_LABEL, parseSpecifyWhen,
  normalizeOptionKeys, optionHasSpecify, branchNo, indexByKey, showQCandidates,
} from "./FeasibilityEditor/data";
import { ChecklistDetail, SpecifyDetail, questionLabel } from "./FeasibilityEditor/QuestionMeta";

// The question table shared by the per-study editor (FeasibilityEditor/SFQQuestionEditor)
// and the template editor (QuestionTemplateManager). Both drive the same row shape; they
// differ only in `editable` (the study editor renders read-only once published) and
// `showActive` (only templates carry isActive, which decides what a study starts with).
//
// Rows are controlled by the parent, which owns dirty tracking and saving. Row shape:
//   { _uid, id?, sectionNo, sectionTitle, questionNo, questionKey, questionText,
//     questionType: "yn"|"ynna"|"numeric"|"text"|"checklist", hasSpecify, specifyLabel,
//     specifyWhen: ["Y"|"N"|"NA"], isActive?, sortOrder,
//     options: [{ _uid, key, label, hasSpecify, specifyLabel, helperText,
//                 showsQuestions: [] }],
//     singleSelect, isRequired }
//
// specifyWhen is an array here and a comma-joined string on the wire — the same
// array↔string split as `options` (an array here, JSON on the wire). Empty means the
// Specify follow-up always shows, which is what pre-existing questions have.

// Column widths of SFQ_Question / SFQ_QuestionTemplate. The save is one transaction,
// so a single over-long field rejects the whole question set — clamp on input instead.
// Keep in sync with db/migrations (032 last widened specifyLabel) and with the server's
// QUESTION_LIMITS (services/Feasibility/fieldLimits.js), which is the backstop under this.
//
// The option fields (label, helperText, and an option's own specifyLabel) are not here:
// they are serialized into [options], which is NVARCHAR(MAX).
const MAX_LEN = {
  sectionTitle: 200,
  questionNo:   10,
  questionKey:  50,
  questionText: 500,
  specifyLabel: 500,
};

const FIELD_LABEL = {
  sectionTitle: "Section title",
  questionNo:   "No.",
  questionKey:  "Question Key",
  questionText: "Question Text",
  specifyLabel: "Specify 라벨",
};

const SectionGroup = styled.div`
  margin-bottom: 20px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  background: #e9ecef;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  font-weight: 600;
  font-size: 13px;
`;

const SectionLabel = styled.span`
  white-space: nowrap;
  color: #495057;
`;

const SectionTitleInput = styled.input`
  flex: 1;
  padding: 3px 8px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 600;
  background: #fff;
  outline: none;
  &:focus { border-color: #0d6efd; }
`;

const AddQBtn = styled.button`
  padding: 3px 10px;
  border: 1px solid #0d6efd;
  background: transparent;
  color: #0d6efd;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #e7f1ff; }
`;

const DelSecBtn = styled.button`
  padding: 2px 8px;
  border: 1px solid #dc3545;
  background: transparent;
  color: #dc3545;
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #dc3545; color: #fff; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  padding: 6px 10px;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  text-align: left;
  font-size: 12px;
  color: #495057;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  vertical-align: middle;
`;

const InlineInput = styled.input`
  width: 100%;
  padding: 3px 6px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  font-size: 13px;
  box-sizing: border-box;
  background: #fff;
  outline: none;
  &:focus { border-color: #0d6efd; }
`;

const TypeSelect = styled.select`
  padding: 3px 6px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  font-size: 13px;
  background: #fff;
`;

const DelRowBtn = styled.button`
  padding: 2px 8px;
  border: 1px solid #dc3545;
  background: transparent;
  color: #dc3545;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: #dc3545; color: #fff; }
`;

const DragHandleTd = styled.td`
  width: 28px;
  padding: 0 4px;
  text-align: center;
  vertical-align: middle;
  border: 1px solid #dee2e6;
  cursor: grab;
  color: #adb5bd;
  font-size: 16px;
  user-select: none;
  &:hover { color: #495057; background: #f8f9fa; }
  &:active { cursor: grabbing; }
`;

// 하위 문항의 들여쓰기. 번호 칸에 얹어, 표 안에서 계층이 한눈에 보이게 한다.
// 왼쪽 세로선은 어느 상위 문항에 딸렸는지를 이어 준다.
const IndentTd = styled.td`
  padding: 6px 10px 6px ${(p) => (p.$child ? 26 : 10)}px;
  border: 1px solid #dee2e6;
  vertical-align: middle;
  position: relative;
  ${(p) => p.$child && `
    &::before {
      content: "";
      position: absolute;
      left: 12px; top: 0; bottom: 0;
      border-left: 2px solid #dee2e6;
    }
  `}
`;

// 들여쓰기 / 내어쓰기. 비활성일 때도 자리를 지켜서 행 높이가 흔들리지 않게 한다.
const LevelBtn = styled.button`
  padding: 0 3px;
  border: 1px solid #ced4da;
  background: #fff;
  color: #495057;
  border-radius: 3px;
  font-size: 11px;
  line-height: 16px;
  cursor: pointer;
  &:hover:enabled { border-color: #0d6efd; color: #0d6efd; background: #f0f6ff; }
  &:disabled { opacity: 0.3; cursor: default; }
`;

const LevelCellTd = styled.td`
  width: 66px;
  padding: 0 2px;
  text-align: center;
  vertical-align: middle;
  border: 1px solid #dee2e6;
  white-space: nowrap;
`;

const SpecifyRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const SpecifyHint = styled.span`
  font-size: 11px;
  color: #adb5bd;
`;

const BottomBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const AddSecBtn = styled.button`
  padding: 5px 14px;
  border: 1px dashed #6c757d;
  background: transparent;
  color: #6c757d;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  &:hover { border-color: #0d6efd; color: #0d6efd; background: #f0f6ff; }
`;

const RenumberBtn = styled.button`
  padding: 5px 14px;
  border: 1px solid #ced4da;
  background: #fff;
  color: #495057;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  &:hover:enabled { border-color: #0d6efd; color: #0d6efd; background: #f0f6ff; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex;
  align-items: center; justify-content: center; z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff; border-radius: 8px; padding: 24px; width: 440px;
  max-width: 90vw; box-shadow: 0 8px 32px rgba(0,0,0,0.18);
`;

const OptionsPanelTd = styled.td`
  padding: 8px 12px 12px 32px;
  background: #f8f9ff;
  border: 1px solid #dee2e6;
  border-top: none;
`;

const OptionsPanelTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6c757d;
  margin-bottom: 6px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
`;

const OptInput = styled.input`
  padding: 3px 6px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  font-size: 12px;
  background: #fff;
  outline: none;
  &:focus { border-color: #0d6efd; }
`;

const OptCheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 11px;
  color: #495057;
  white-space: nowrap;
  cursor: pointer;
`;

const DelOptBtn = styled.button`
  padding: 2px 6px;
  border: 1px solid #dc3545;
  background: transparent;
  color: #dc3545;
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: #dc3545; color: #fff; }
`;

const AddOptBtn = styled.button`
  margin-top: 4px;
  padding: 2px 10px;
  border: 1px dashed #6c757d;
  background: transparent;
  color: #6c757d;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  &:hover { border-color: #0d6efd; color: #0d6efd; }
`;

const DisablesBtn = styled.button`
  padding: 2px 6px;
  border: 1px solid ${(p) => (p.$active ? "#0d6efd" : "#ced4da")};
  background: ${(p) => (p.$active ? "#e7f1ff" : "transparent")};
  color: ${(p) => (p.$active ? "#0d6efd" : "#6c757d")};
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { border-color: #0d6efd; color: #0d6efd; }
`;

// ── Shows Q 고르기 ────────────────────────────────────────────────────────────
//
// 후보가 섹션을 넘나들기 때문에 번호만으로는 서로 구별되지 않는다 — "2번"은 섹션마다
// 하나씩 있다. 기본 문항 세트만 해도 상위 문항 하나의 후보가 스무 개 가까이 되어,
// 토글을 한 줄에 늘어놓으면 이미 빽빽한 옵션 행이 넘친다. 그래서 개수만 접어 두고,
// 펼치면 섹션으로 묶인 목록이 번호와 문구를 함께 보여 준다 — 고르는 사람이 읽는 것은
// key도 번호도 아니고 문구다.
const ShowsQWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ShowsQBtn = styled.button`
  padding: 2px 8px;
  border: 1px solid ${(p) => (p.$active ? "#0d6efd" : "#ced4da")};
  background: ${(p) => (p.$active ? "#e7f1ff" : "transparent")};
  color: ${(p) => (p.$active ? "#0d6efd" : "#6c757d")};
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  &:hover:enabled { border-color: #0d6efd; color: #0d6efd; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const ShowsQPop = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 20;
  width: 320px;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
  padding: 4px 0;
`;

const ShowsQSectionHead = styled.div`
  padding: 5px 10px 2px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  color: #adb5bd;
  border-top: 1px solid #f1f3f5;
  &:first-child { border-top: none; }
`;

const ShowsQItem = styled.label`
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 3px 10px;
  font-size: 12px;
  color: #495057;
  cursor: pointer;
  &:hover { background: #f0f6ff; }
`;

const ShowsQNo = styled.b`
  flex: none;
  color: #6c757d;
`;

const ShowsQEmpty = styled.div`
  padding: 8px 10px;
  font-size: 11px;
  color: #adb5bd;
`;

// candidates: [{ key, no, text, sectionNo, sectionTitle }] — 이 옵션이 열 수 있는
// 문항들, 표시 순서 그대로. selected: 고른 questionKey 배열.
function ShowsQPicker({ candidates, selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const chosen = candidates.filter((q) => selected.includes(q.key));

  // 섹션 묶음은 후보 순서를 그대로 따라간다 — 후보는 표시 순서이고 한 섹션의 문항은
  // 붙어 있으므로, 이어지는 구간을 끊는 것만으로 섹션별 묶음이 된다.
  const groups = [];
  for (const q of candidates) {
    const last = groups[groups.length - 1];
    if (last && last.sectionNo === q.sectionNo) last.items.push(q);
    else groups.push({ sectionNo: q.sectionNo, sectionTitle: q.sectionTitle, items: [q] });
  }

  return (
    <ShowsQWrap ref={wrapRef}>
      <ShowsQBtn
        type="button"
        $active={chosen.length > 0}
        disabled={!candidates.length}
        onClick={() => setOpen((v) => !v)}
        title={chosen.length
          ? `선택 시 표시: ${chosen.map((q) => `${q.no}. ${q.text || q.key}`).join(", ")}`
          : "이 옵션을 선택했을 때 나타날 문항을 고릅니다."}
      >
        Shows Q: {chosen.length ? `${chosen.length}개` : "없음"} ▾
      </ShowsQBtn>
      {open && (
        <ShowsQPop>
          {!candidates.length && <ShowsQEmpty>고를 수 있는 문항이 없습니다.</ShowsQEmpty>}
          {groups.map((g) => (
            <React.Fragment key={g.items[0].key}>
              <ShowsQSectionHead>
                Section {g.sectionNo}{g.sectionTitle ? ` · ${g.sectionTitle}` : ""}
              </ShowsQSectionHead>
              {g.items.map((q) => (
                <ShowsQItem key={q.key}>
                  <input
                    type="checkbox"
                    checked={selected.includes(q.key)}
                    onChange={() => onToggle(q.key)}
                  />
                  <ShowsQNo>{q.no}.</ShowsQNo>
                  <span>{q.text || q.key}</span>
                </ShowsQItem>
              ))}
            </React.Fragment>
          ))}
        </ShowsQPop>
      )}
    </ShowsQWrap>
  );
}

export function uid() {
  return `_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function sanitizeKey(raw) {
  return raw.trim();
}

// `key_n`, trimmed so the result still fits questionKey's column.
function suffixedKey(key, n) {
  const suffix = `_${n}`;
  return `${key.slice(0, MAX_LEN.questionKey - suffix.length)}${suffix}`;
}

// Option keys are assigned automatically and never shown in the editor, so a key that
// repeats — only possible in rows authored back when the key was typed by hand — would
// be a save blocker nobody can clear. Rename the later ones. Both options already read
// as checked from the same answer key, so the first one keeps the recorded answer and
// nothing gets less accurate than it already was.
function dedupeOptionKeys(list) {
  const seen = new Set();
  return list.map((o) => {
    const key = o.key;
    if (!seen.has(key)) { seen.add(key); return o; }
    let n = 2;
    while (seen.has(`${key}_${n}`)) n++;
    seen.add(`${key}_${n}`);
    return { ...o, key: `${key}_${n}` };
  });
}

// options are stored as a JSON string in the DB and edited as an array. An option
// without a key is unusable — a checklist answer is the list of checked keys, so
// blank-keyed options all read as checked together — hence keys are backfilled on the
// way in, matching what the renderer does with the same rows (data.normalizeOptionKeys).
export function parseOptions(raw) {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : (() => {
    try { return JSON.parse(raw); } catch { return null; }
  })();
  if (!Array.isArray(arr)) return [];
  return dedupeOptionKeys(normalizeOptionKeys(arr)).map((o) => ({ ...o, _uid: o._uid || uid() }));
}

// The Specify trigger codes a type can actually offer — dropping NA when a question is
// switched back to plain Yes/No, and everything when it stops being a Y/N question.
function legalSpecifyWhen(questionType, specifyWhen) {
  const codes = YN_CODES[questionType] || [];
  return parseSpecifyWhen(specifyWhen).filter((c) => codes.includes(c));
}

// Editor array → the comma-joined string the column stores. Normalized to Y,N,NA order
// so an unchanged row doesn't look dirty just because the codes were clicked in
// another order.
function formatSpecifyWhen(codes) {
  const set = new Set(codes);
  const ordered = ["Y", "N", "NA"].filter((c) => set.has(c));
  return ordered.length ? ordered.join(",") : null;
}

// DB rows (SFQ_Question or SFQ_QuestionTemplate) → editor rows.
export function toEditorRows(dbRows) {
  return (dbRows || []).map((q, i) => ({
    ...q,
    _uid: uid(),
    sectionTitle: q.sectionTitle || "",
    // Rows saved before the hierarchy existed have no parentKey → 상위 문항.
    parentKey: q.parentKey || null,
    sortOrder: q.sortOrder != null ? q.sortOrder : i,
    options: parseOptions(q.options),
    specifyWhen: parseSpecifyWhen(q.specifyWhen),
    singleSelect: !!q.singleSelect,
    // Rows saved before the column existed have no isRequired → required.
    isRequired: q.isRequired !== false && q.isRequired !== 0,
    isActive: q.isActive !== false && q.isActive !== 0,
  }));
}

// Editor rows → the payload both save endpoints take. sortOrder is re-derived from
// display order, and options are only meaningful for checklists.
export function toQuestionPayload(rows, { includeActive = false } = {}) {
  return rows.map((r, i) => ({
    sectionNo:    r.sectionNo,
    sectionTitle: r.sectionTitle || "",
    questionNo:   r.questionNo   || "",
    questionKey:  (r.questionKey  || "").trim(),
    parentKey:    (r.parentKey    || "").trim() || null,
    questionText: (r.questionText || "").trim(),
    questionType: r.questionType,
    hasSpecify:   !!r.hasSpecify,
    specifyLabel: r.specifyLabel || null,
    specifyWhen:  formatSpecifyWhen(legalSpecifyWhen(r.questionType, r.specifyWhen)),
    sortOrder:    i,
    isRequired:   !!r.isRequired,
    ...(includeActive ? { isActive: r.isActive !== false } : {}),
    singleSelect: r.questionType === "checklist" ? !!r.singleSelect : false,
    // hasSpecify is written out explicitly, including for options that predate it —
    // optionHasSpecify resolves those from specifyLabel, so a row saved untouched keeps
    // the follow-up input it already showed.
    options:      r.questionType === "checklist"
      ? JSON.stringify(normalizeOptionKeys(r.options || [])
          .map(({ _uid, ...o }) => ({ ...o, hasSpecify: optionHasSpecify(o) })))
      : null,
  }));
}

export function blankRow(sectionNo, sortOrder) {
  return {
    _uid: uid(),
    id: undefined,
    sectionNo,
    sectionTitle: "",
    questionNo: "1",
    questionKey: `q_custom_${Date.now()}`,
    parentKey: null,
    questionText: "",
    questionType: "yn",
    hasSpecify: false,
    specifyLabel: "",
    specifyWhen: [],
    isActive: true,
    sortOrder,
    options: [],
    singleSelect: false,
    isRequired: true,
  };
}

// ── Applying a template / prior version ───────────────────────────────────────

// Drop everything and start from the incoming rows.
export function applyOverwrite(incomingRows) {
  return incomingRows.map((r, i) => ({ ...r, _uid: uid(), id: undefined, sortOrder: i }));
}

// Append incoming rows after the current ones. Incoming sections are renumbered so
// they land after the existing ones, and a questionKey that collides is renamed
// (key → key_2). Both things that reference a question by key — an option's
// showsQuestions and a 하위 문항's parentKey — are rewritten to match, so conditional
// display and the hierarchy keep pointing at the rows they were authored against.
export function applyAppend(currentRows, incomingRows) {
  const keys = new Set(currentRows.map((r) => r.questionKey).filter(Boolean));
  let nextSec = currentRows.length
    ? Math.max(...currentRows.map((r) => r.sectionNo || 1))
    : 0;

  const sectionMap = {};
  for (const r of incomingRows) {
    if (sectionMap[r.sectionNo] == null) sectionMap[r.sectionNo] = ++nextSec;
  }

  const renamed = {};
  for (const r of incomingRows) {
    const key = r.questionKey || "";
    if (!key) continue;
    if (!keys.has(key)) { keys.add(key); continue; }
    // The suffix has to fit inside the column too, so a key already at the limit is
    // trimmed to make room for it rather than pushed past it.
    let n = 2;
    let next = suffixedKey(key, n);
    while (keys.has(next)) next = suffixedKey(key, ++n);
    renamed[key] = next;
    keys.add(next);
  }

  const appended = incomingRows.map((r, i) => ({
    ...r,
    _uid: uid(),
    id: undefined,
    sectionNo: sectionMap[r.sectionNo],
    questionKey: renamed[r.questionKey] || r.questionKey,
    parentKey: r.parentKey ? (renamed[r.parentKey] || r.parentKey) : null,
    sortOrder: currentRows.length + i,
    options: (r.options || []).map((o) => ({
      ...o,
      _uid: uid(),
      showsQuestions: (o.showsQuestions || []).map((k) => renamed[k] || k),
    })),
  }));

  return [...currentRows, ...appended];
}

// Returns the first row that fails validation, or null. Both editors require every
// question to carry a key and some text, and every checklist option a label and a key
// that is unique within its question — the answer stores the checked options by key,
// so a blank or shared key makes those options toggle and render as one.
//
// Lengths are re-checked here even though the inputs carry maxLength, because maxLength
// only governs what a person types into that input: a row applied from a template or a
// prior version, or a key the append renamer lengthened, never passes through it. An
// over-long field is one the server rejects for the whole set, so it is caught here with
// the offending question named rather than as a failed save.
export function findInvalidRow(rows) {
  for (const [i, r] of rows.entries()) {
    if (!r.questionKey?.trim())  return { row: r, field: "questionKey" };
    if (!r.questionText?.trim()) return { row: r, field: "questionText" };
    for (const [field, max] of Object.entries(MAX_LEN)) {
      const len = (r[field] || "").length;
      if (len > max) {
        return {
          row: r,
          field,
          message: `${i + 1}번 문항의 ${FIELD_LABEL[field]}이(가) 너무 깁니다.\n` +
                   `${max}자 이내로 줄여 주세요. (현재 ${len}자)`,
        };
      }
    }
    if (r.questionType === "checklist" && badOptionUids(r.options).size) {
      return { row: r, field: "optionKey" };
    }
    if (r.questionType === "checklist") {
      const blankLabels = blankOptionLabelUids(r.options);
      if (blankLabels.size) {
        return { row: r, field: "optionLabel", optionUid: blankLabels.values().next().value };
      }
    }
  }

  // The hierarchy is checked over the whole set rather than per row, since every rule
  // is about a row's relation to another one. normalizeHierarchy already repairs these
  // after each structural edit, so reaching here means a row arrived broken from
  // somewhere else — a hand-edited template, or a set saved by an older client. Better
  // to name it than to let the server reject the whole save.
  const byKey = {};
  for (const r of rows) if (r.questionKey) byKey[r.questionKey] = r;
  for (const [i, r] of rows.entries()) {
    if (!r.parentKey) continue;
    const parent = byKey[r.parentKey];
    const problem =
      !parent                            ? "상위 문항을 찾을 수 없습니다."
      : parent === r                     ? "자기 자신을 상위 문항으로 가리키고 있습니다."
      : parent.parentKey                 ? "상위 문항이 이미 하위 문항입니다. 문항은 2단계까지만 만들 수 있습니다."
      : parent.sectionNo !== r.sectionNo ? "상위 문항과 섹션이 다릅니다."
      : null;
    if (problem) {
      return { row: r, field: "parentKey", message: `${i + 1}번 문항의 ${problem}` };
    }
  }

  // No.가 겹치면 두 문항이 화면에도 PDF에도 같은 번호로 찍힌다. 응답자가 어느 문항을
  // 두고 하는 말인지 가리킬 수 없고, Show Q 목록에서도 구별되지 않는다. 계층이 성립한
  // 뒤에야 어느 묶음에서 겹치는지 정해지므로 위 검사 다음에 온다.
  const dupeNos = duplicateNoUids(rows);
  if (dupeNos.size) {
    const i = rows.findIndex((r) => dupeNos.has(r._uid));
    const r = rows[i];
    const where = r.parentKey
      ? "같은 상위 문항의 다른 하위 문항"
      : "같은 섹션의 다른 문항";
    return {
      row: r,
      field: "questionNo",
      message: `${i + 1}번 문항의 No.(${(r.questionNo || "").trim()})가 ${where}과 겹칩니다.\n` +
               "빨간 테두리로 표시된 번호를 고치거나, " +
               "\"No. 순서대로 다시 매기기\"로 한 번에 정리해 주세요.",
    };
  }
  return null;
}

export const INVALID_ROW_MESSAGE = {
  questionKey:  "Question Key는 필수입니다. 모든 질문에 key를 입력해 주세요.",
  questionText: "Question Text는 필수입니다. 모든 질문에 텍스트를 입력해 주세요.",
  optionKey:    "Checklist 옵션의 key(자동 부여)가 비어 있거나 중복입니다.\n" +
                "빨간 테두리로 표시된 옵션을 삭제한 뒤 다시 추가해 주세요.",
  optionLabel:  "Checklist 옵션의 Label text는 필수입니다.\n" +
                "빨간 테두리로 표시된 옵션에 텍스트를 입력해 주세요.",
};

// The message to show for a findInvalidRow result — its own when it carries one (the
// length failures, which name the question and the count), else the fixed text for the
// field.
export function invalidRowMessage(invalid) {
  return invalid.message || INVALID_ROW_MESSAGE[invalid.field];
}

// 한 문항의 No.가 겹치는지 따지는 범위. 번호는 상위 문항이면 섹션 안에서, 하위 문항이면
// 같은 상위 문항의 하위들 안에서 매겨지므로(renumberQuestionNos), 겹침도 그 안에서만
// 따진다. 섹션 1의 "2"와 섹션 2의 "2"는 서로 다른 문항이고, "3-1"과 "4-1"도 마찬가지다.
const noScopeOf = (r) => (r.parentKey ? `p:${r.parentKey}` : `s:${r.sectionNo}`);

// _uids of the rows whose No. repeats inside its numbering scope — every row of the
// clash, not just the later one, since fixing it means looking at both.
//
// 빈 번호는 세지 않는다. 아직 번호를 매기지 않았다는 뜻이고, "No. 순서대로 다시 매기기"가
// 채워 주며, 서버도 빈 값이면 순서로 대신한다.
//
// Shared with the editor so the rows the save complains about are the ones outlined in red.
export function duplicateNoUids(rows) {
  const buckets = new Map();
  for (const r of rows || []) {
    const no = (r.questionNo || "").trim();
    if (!no) continue;
    const bucket = `${noScopeOf(r)}\u0000${no}`;
    if (!buckets.has(bucket)) buckets.set(bucket, []);
    buckets.get(bucket).push(r._uid);
  }
  const dupes = new Set();
  for (const uids of buckets.values()) {
    if (uids.length > 1) for (const u of uids) dupes.add(u);
  }
  return dupes;
}

// 같은 묶음에서 아직 쓰지 않은 가장 작은 번호. 개수+1로는 모자라다 — 1,2,3에서 2를 지우면
// 개수+1이 3이라 이미 있는 번호와 부딪힌다.
function nextFreeNo(peers) {
  const taken = new Set(peers.map((r) => (r.questionNo || "").trim()).filter(Boolean));
  let n = 1;
  while (taken.has(String(n))) n++;
  return String(n);
}

// _uids of the options whose key is blank or a duplicate. Shared with OptionsEditor so
// the options the save complains about are the ones outlined in red.
export function badOptionUids(options) {
  const seen = new Set();
  const bad = new Set();
  for (const o of options || []) {
    const key = (o.key || "").trim();
    if (!key || seen.has(key)) bad.add(o._uid);
    else seen.add(key);
  }
  return bad;
}

function blankOptionLabelUids(options) {
  return new Set((options || [])
    .filter((o) => !(o.label || "").trim())
    .map((o) => o._uid));
}

// ── Sub-components ────────────────────────────────────────────────────────────

export function KeyEditModal({ target, rows, onConfirm, onClose }) {
  const [newKey, setNewKey] = useState(target.currentKey);

  const refs = [];
  for (const row of rows) {
    for (const opt of (row.options || [])) {
      if ((opt.showsQuestions || []).includes(target.currentKey)) {
        refs.push({ questionText: row.questionText || row.questionKey, optLabel: opt.label || opt.key });
      }
    }
  }

  // A 하위 문항 points at its parent by the very key being renamed, so the rename has to
  // carry the hierarchy too — and the author should see that before confirming, since
  // nothing else on this screen says these rows are attached to the one being edited.
  const children = rows.filter((r) => r.parentKey === target.currentKey);

  const sanitized = sanitizeKey(newKey);
  const isDupe = rows.some((r) => r._uid !== target._uid && r.questionKey === sanitized);
  const isEmpty = sanitized === "";
  const isTooLong = sanitized.length > MAX_LEN.questionKey;
  const blocked = isDupe || isEmpty || isTooLong;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>Question Key 수정</h3>
        {target.currentKey && (
          <div style={{ fontSize: 12, color: "#6c757d", marginBottom: 8 }}>
            현재 key: <code>{target.currentKey}</code>
          </div>
        )}
        <input
          autoFocus
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onBlur={(e) => setNewKey(sanitizeKey(e.target.value))}
          placeholder="key 입력"
          maxLength={MAX_LEN.questionKey}
          style={{ width: "100%", fontFamily: "monospace", padding: "4px 8px", border: `1px solid ${blocked ? "#dc3545" : "#ced4da"}`, borderRadius: 3, fontSize: 13, boxSizing: "border-box" }}
        />
        <div style={{ fontSize: 11, color: "#6c757d", marginTop: 4 }}>
          저장 시 적용: <code>{sanitized || "(비어 있음)"}</code>
        </div>
        {isDupe && <div style={{ color: "#dc3545", fontSize: 12, marginTop: 4 }}>이미 사용 중인 key입니다.</div>}
        {isTooLong && (
          <div style={{ color: "#dc3545", fontSize: 12, marginTop: 4 }}>
            key는 {MAX_LEN.questionKey}자 이내여야 합니다. (현재 {sanitized.length}자)
          </div>
        )}
        {children.length > 0 && (
          <div style={{ marginTop: 12, padding: "8px 10px", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 4, fontSize: 12 }}>
            <strong>아래 하위 문항의 연결도 자동으로 업데이트됩니다 ({children.length}개):</strong>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {children.map((r) => (
                <li key={r._uid}>{r.questionText || r.questionKey}</li>
              ))}
            </ul>
          </div>
        )}
        {refs.length > 0 && (
          <div style={{ marginTop: 12, padding: "8px 10px", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 4, fontSize: 12 }}>
            <strong>아래 옵션의 Shows Q 참조도 자동으로 업데이트됩니다 ({refs.length}개):</strong>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {refs.map((r, i) => <li key={i}>{r.questionText} → "{r.optLabel}"</li>)}
            </ul>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={{ padding: "5px 14px", border: "1px solid #ced4da", borderRadius: 4, background: "#fff", cursor: "pointer" }}>취소</button>
          <button
            type="button"
            disabled={blocked || sanitized === target.currentKey}
            onClick={() => onConfirm(sanitized)}
            style={{ padding: "5px 14px", border: "1px solid #0d6efd", borderRadius: 4, background: "#0d6efd", color: "#fff", cursor: "pointer", opacity: (blocked || sanitized === target.currentKey) ? 0.5 : 1 }}
          >
            적용
          </button>
        </div>
      </ModalBox>
    </ModalOverlay>
  );
}

// showQTargets: [{ key, no, text, sectionNo, sectionTitle }] — the questions an option
// may reveal, already narrowed to what the hierarchy allows (data.showQCandidates) and
// in display order. `no` is the number path within its section ("3" or "3-1"); the
// section itself comes from the group heading the picker puts it under.
function OptionsEditor({ options, onChange, showQTargets = [], rowRefs, rowUid }) {
  function updateOpt(_uid, field, value) {
    onChange(options.map((o) => (o._uid === _uid ? { ...o, [field]: value } : o)));
  }
  function deleteOpt(_uid) {
    onChange(options.filter((o) => o._uid !== _uid));
  }
  function addOpt() {
    // Never blank: options are identified by key in the stored answer, so two keyless
    // options would toggle as one.
    const taken = new Set(options.map((o) => (o.key || "").trim()).filter(Boolean));
    let n = options.length + 1;
    while (taken.has(`opt_${n}`)) n++;
    onChange([...options, { _uid: uid(), key: `opt_${n}`, label: "", hasSpecify: false,
                            specifyLabel: "", helperText: "", showsQuestions: [] }]);
  }
  function toggleShowsQ(_uid, qKey) {
    const opt = options.find((o) => o._uid === _uid);
    if (!opt) return;
    const cur = opt.showsQuestions || [];
    const next = cur.includes(qKey) ? cur.filter((k) => k !== qKey) : [...cur, qKey];
    updateOpt(_uid, "showsQuestions", next);
  }

  const badKeys = badOptionUids(options);
  const blankLabels = blankOptionLabelUids(options);

  return (
    <div>
      <OptionsPanelTitle>Checklist Options</OptionsPanelTitle>
      {options.map((opt) => {
        const hasSpecify = optionHasSpecify(opt);
        return (
          <OptionRow key={opt._uid}>
            <OptInput
              ref={(el) => { if (rowRefs) rowRefs.current[`${rowUid}_optionLabel_${opt._uid}`] = el; }}
              value={opt.label || ""}
              onChange={(e) => updateOpt(opt._uid, "label", e.target.value)}
              placeholder="Label text"
              aria-invalid={blankLabels.has(opt._uid) || badKeys.has(opt._uid)}
              title={badKeys.has(opt._uid)
                ? "이 옵션의 key가 비어 있거나 중복입니다. 옵션을 지웠다 다시 추가해 주세요."
                : blankLabels.has(opt._uid)
                  ? "Label text를 입력해 주세요."
                  : undefined}
              style={{ flex: 2, minWidth: 120,
                       borderColor: (blankLabels.has(opt._uid) || badKeys.has(opt._uid)) ? "#dc3545" : undefined }}
            />
            <OptCheckLabel title="체크하면 이 옵션을 선택했을 때 자유 입력란이 나타납니다.">
              <input
                type="checkbox"
                checked={hasSpecify}
                onChange={(e) => updateOpt(opt._uid, "hasSpecify", e.target.checked)}
              />
              입력란
            </OptCheckLabel>
            {hasSpecify && (
              <OptInput
                value={opt.specifyLabel || ""}
                onChange={(e) => updateOpt(opt._uid, "specifyLabel", e.target.value)}
                placeholder="입력란 placeholder (선택)"
                maxLength={MAX_LEN.specifyLabel}
                style={{ flex: 2, minWidth: 180 }}
              />
            )}
            <OptInput
              value={opt.helperText || ""}
              onChange={(e) => updateOpt(opt._uid, "helperText", e.target.value)}
              placeholder="Helper text (항상 표시되는 안내문)"
              style={{ flex: 2, minWidth: 180 }}
            />
            <ShowsQPicker
              candidates={showQTargets}
              selected={opt.showsQuestions || []}
              onToggle={(qKey) => toggleShowsQ(opt._uid, qKey)}
            />
            <DelOptBtn type="button" onClick={() => deleteOpt(opt._uid)}>✕</DelOptBtn>
          </OptionRow>
        );
      })}
      <AddOptBtn type="button" onClick={addOpt}>+ Add Option</AddOptBtn>
    </div>
  );
}

function groupBySec(rows) {
  const order = [];
  const map = {};
  for (const r of rows) {
    if (!map[r.sectionNo]) {
      map[r.sectionNo] = [];
      order.push(r.sectionNo);
    }
    map[r.sectionNo].push(r);
  }
  return order.map((no) => ({ no, rows: map[no] }));
}

// ── 상위 / 하위 문항 ──────────────────────────────────────────────────────────
//
// A row whose `parentKey` names another row's `questionKey` is that row's 하위 문항.
// The list itself stays flat and ordered — nesting is drawn from parentKey, but
// position is still the array's, which is what keeps groupBySec, resequenceSections,
// the drag reorder and toQuestionPayload's `sortOrder: i` all working unchanged.
//
// Three invariants hold everywhere below, and normalizeHierarchy is what restores them:
//   1. a row with a parentKey points at a row that has none  (two levels, no deeper)
//   2. parent and child share a sectionNo
//   3. children sit in one unbroken run directly after their parent
//
// The API re-checks all three before it rewrites the set
// (services/Feasibility/questionStructure.js) — that one rejects rather than repairs,
// since anything reaching it in a broken shape did not come from this editor.

export const isChild = (row) => !!(row && row.parentKey);

// The rows whose parentKey names this row, in display order.
export function childrenOf(rows, row) {
  const key = row && row.questionKey;
  return key ? rows.filter((r) => r.parentKey === key) : [];
}

// A row plus its children — the unit that moves, and that gets deleted, together.
export function blockOf(rows, row) {
  return isChild(row) ? [row] : [row, ...childrenOf(rows, row)];
}

// Restore the three invariants, in display order. A parentKey that can't be honoured —
// naming a row that is missing, is itself a child, sits in another section, or comes
// later in the list — is dropped, promoting the row rather than losing it. Surviving
// children are then gathered directly behind their parent.
//
// Unchanged rows come back by reference (the same rule renumberQuestionNos and
// resequenceSections follow) so a run that changes nothing leaves every row identity
// intact and can be detected by the caller — both parents treat any onChange as dirty.
export function normalizeHierarchy(rows) {
  const byKey = {};
  for (const r of rows) if (r.questionKey) byKey[r.questionKey] = r;

  const indexOf = new Map();
  rows.forEach((r, i) => {
    if (r.questionKey && !indexOf.has(r.questionKey)) indexOf.set(r.questionKey, i);
  });

  const fixed = rows.map((r, i) => {
    if (!r.parentKey) return r.parentKey === null ? r : { ...r, parentKey: null };
    const parent = byKey[r.parentKey];
    const ok = parent && parent !== r && !parent.parentKey &&
               parent.sectionNo === r.sectionNo && indexOf.get(r.parentKey) < i;
    return ok ? r : { ...r, parentKey: null };
  });

  // Gather: walk the top-level rows in order and pull each one's children in behind it.
  // Children already in place are visited exactly once, so this is a stable reordering.
  const claimed = new Set();
  const ordered = [];
  for (const r of fixed) {
    if (r.parentKey) continue;
    ordered.push(r);
    claimed.add(r);
    for (const child of fixed) {
      if (child.parentKey === r.questionKey && !claimed.has(child)) {
        ordered.push(child);
        claimed.add(child);
      }
    }
  }
  // A child whose parent was dropped above is already top-level, so nothing is orphaned;
  // this only catches duplicate questionKeys, which validation reports separately.
  for (const r of fixed) if (!claimed.has(r)) ordered.push(r);

  return ordered.every((r, i) => r === rows[i]) ? rows : ordered;
}

// Section numbers have to stay contiguous (1,2,3…). Deleting a section — or the
// last question in one — leaves a hole, so renumber in display order afterwards.
function resequenceSections(rows) {
  const map = {};
  let next = 0;
  return rows.map((r) => {
    if (map[r.sectionNo] == null) map[r.sectionNo] = ++next;
    return map[r.sectionNo] === r.sectionNo ? r : { ...r, sectionNo: map[r.sectionNo] };
  });
}

// The No. column is free text — it survives drags, deletions and appended templates
// untouched, so the printed numbering drifts away from the order the questions are
// actually in. This rewrites it in display order: 상위 문항 count 1,2,3… within each
// section, and 하위 문항 restart at 1 under every parent —
//
//   1. 상위항목1
//       1. 하위항목1
//       2. 하위항목2
//   2. 상위항목2
//
// so a sub-question's number is only unique next to its parent's. Everything that
// prints one on its own resolves the full path through data.noPath instead.
//
// Rows whose number is already right are returned as-is, so a run that changes
// nothing leaves every row identity intact.
export function renumberQuestionNos(rows) {
  const sectionCounters = {};
  const childCounters = {};
  return rows.map((r) => {
    // Children are counted per parent rather than per run, so ones scattered by a
    // broken parentKey still number consistently. normalizeHierarchy gathers them anyway.
    const next = r.parentKey
      ? String((childCounters[r.parentKey] = (childCounters[r.parentKey] || 0) + 1))
      : String((sectionCounters[r.sectionNo] = (sectionCounters[r.sectionNo] || 0) + 1));
    return r.questionNo === next ? r : { ...r, questionNo: next };
  });
}

// ── The editor ────────────────────────────────────────────────────────────────

export default function QuestionRowsEditor({
  rows,
  onChange,
  editable = true,
  showActive = false,
  rowRefs,
}) {
  const [keyEditTarget, setKeyEditTarget] = useState(null);
  const [dragOver, setDragOver] = useState({ uid: null, half: null });
  const dragSrcUid = useRef(null);

  const byKey = indexByKey(rows);
  const dupeNoUids = duplicateNoUids(rows);

  // Read-only rendering names the questions an option reveals ("3. Do you …") rather
  // than their bare keys. The editable table shows numbers instead — the toggles there
  // have to stay narrow enough to sit in a row.
  const resolveQuestion = (key) => questionLabel(byKey[key], byKey);

  function updateRow(_uid, field, value) {
    onChange(rows.map((r) => (r._uid === _uid ? { ...r, [field]: value } : r)));
  }

  // Changing the type has to drop Specify triggers the new type can't offer, or a
  // ynna → yn switch leaves an unreachable NA trigger behind.
  function changeQuestionType(_uid, questionType) {
    onChange(rows.map((r) => (r._uid === _uid
      ? { ...r, questionType, specifyWhen: legalSpecifyWhen(questionType, r.specifyWhen) }
      : r)));
  }

  function toggleSpecifyWhen(row, code) {
    const current = parseSpecifyWhen(row.specifyWhen);
    updateRow(row._uid, "specifyWhen", current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code]);
  }

  function updateSectionTitle(sectionNo, title) {
    onChange(rows.map((r) => (r.sectionNo === sectionNo ? { ...r, sectionTitle: title } : r)));
  }

  function addQuestion(sectionNo) {
    const secRows = rows.filter((r) => r.sectionNo === sectionNo && !r.parentKey);
    const newRow = {
      ...blankRow(sectionNo, rows.length),
      sectionTitle: secRows[0]?.sectionTitle || "",
      questionNo: nextFreeNo(secRows),
    };
    const lastIdx = rows.reduce((acc, r, i) => (r.sectionNo === sectionNo ? i : acc), -1);
    const next = [...rows];
    next.splice(lastIdx + 1, 0, newRow);
    onChange(next);
  }

  // A 하위 문항 of `parent`, appended after its existing children so the block stays
  // contiguous. Its key is derived from the parent's, which is what makes a set of keys
  // readable once questions nest — q_staff → q_staff_1, q_staff_2.
  function addSubQuestion(parent) {
    const siblings = childrenOf(rows, parent);
    const taken = new Set(rows.map((r) => r.questionKey).filter(Boolean));
    let n = siblings.length + 1;
    let key = suffixedKey(parent.questionKey, n);
    while (taken.has(key)) key = suffixedKey(parent.questionKey, ++n);

    const newRow = {
      ...blankRow(parent.sectionNo, rows.length),
      sectionTitle: parent.sectionTitle || "",
      questionKey: parent.questionKey ? key : `q_custom_${Date.now()}`,
      parentKey: parent.questionKey,
      questionNo: nextFreeNo(siblings),
    };
    const lastOfBlock = blockOf(rows, parent).slice(-1)[0];
    const next = [...rows];
    next.splice(rows.indexOf(lastOfBlock) + 1, 0, newRow);
    onChange(next);
  }

  // Settle the result of a structural edit: restore the hierarchy invariants, then drop
  // the Show Q references that the new shape puts out of scope — telling the author which
  // ones first, since nothing else on screen would show them disappearing. Returns the
  // rows to commit, or null if the author backed out.
  //
  // A reference can only fall out of scope because of a hierarchy change: with a flat
  // list every question is a candidate for every other, so a set authored before any of
  // this existed has nothing to prune and never raises the prompt.
  function applyStructuralChange(nextRows) {
    const normalized = normalizeHierarchy(nextRows);
    const dropped = [];

    const pruned = normalized.map((row) => {
      if (!row.options?.length) return row;
      const allowed = new Set(showQCandidates(normalized, row).map((q) => q.questionKey));
      let touched = false;
      const options = row.options.map((opt) => {
        const refs = opt.showsQuestions || [];
        const keep = refs.filter((k) => allowed.has(k));
        if (keep.length === refs.length) return opt;
        touched = true;
        for (const k of refs) {
          if (!allowed.has(k)) {
            dropped.push(`${questionLabel(row, byKey)} → "${opt.label || opt.key}" → ` +
                         `${byKey[k] ? questionLabel(byKey[k], byKey) : k}`);
          }
        }
        return { ...opt, showsQuestions: keep };
      });
      return touched ? { ...row, options } : row;
    });

    if (dropped.length && !window.confirm(
      "이 변경으로 아래 Show Q 설정이 허용 범위를 벗어나 해제됩니다.\n" +
      "상위 문항은 다른 상위 문항과 자기 하위 문항만, 하위 문항은 같은 상위의 " +
      "하위 문항만 열 수 있습니다.\n\n" +
      dropped.map((d) => `· ${d}`).join("\n") +
      "\n\n계속하시겠습니까?"
    )) return null;

    return pruned;
  }

  // ── 들여쓰기 / 내어쓰기 ─────────────────────────────────────────────────────
  //
  // The buttons are the deliberate way to change a question's level; a drag decides the
  // level from where it lands, which is convenient but not always what was meant.

  // A row can become a sub-question when the row directly above it, in the same section,
  // is a 상위 문항 — that row is the only parent it could get without moving. A row that
  // already has children is refused rather than dragging them to a third level.
  function indentTargetOf(row) {
    if (!row || row.parentKey) return null;
    if (childrenOf(rows, row).length) return null;
    const prev = rows[rows.indexOf(row) - 1];
    if (!prev || prev.sectionNo !== row.sectionNo) return null;
    return prev.parentKey ? byKey[prev.parentKey] : prev;
  }

  function indentRow(row) {
    const parent = indentTargetOf(row);
    if (!parent) return;
    const withParent = rows.map((r) =>
      (r._uid === row._uid ? { ...r, parentKey: parent.questionKey } : r));
    const settled = applyStructuralChange(withParent);
    if (settled) onChange(settled);
  }

  // Outdent lands the row directly after its former parent's remaining children, so the
  // siblings that followed it stay attached to that parent instead of being split off.
  function outdentRow(row) {
    if (!row.parentKey) return;
    const parent = byKey[row.parentKey];
    const promoted = { ...row, parentKey: null };
    const without = rows.filter((r) => r._uid !== row._uid);
    const anchor = parent ? blockOf(without, parent).slice(-1)[0] : null;
    const at = anchor ? without.indexOf(anchor) + 1 : without.length;
    const next = [...without];
    next.splice(at, 0, promoted);
    const settled = applyStructuralChange(next);
    if (settled) onChange(settled);
  }

  function addSection() {
    const maxSec = rows.length ? Math.max(...rows.map((r) => r.sectionNo)) : 0;
    onChange([...rows, blankRow(maxSec + 1, rows.length)]);
  }

  // Hand-typed numbers are overwritten, so this asks first — and stays quiet when the
  // numbering is already correct, since the parent treats any onChange as a change.
  function renumberQuestions() {
    const next = renumberQuestionNos(rows);
    if (next.every((r, i) => r === rows[i])) {
      window.alert("모든 섹션의 No.가 이미 순서대로입니다.");
      return;
    }
    if (!window.confirm(
      "모든 섹션의 No.를 각각 1, 2, 3… 순서로 다시 매깁니다.\n" +
      "직접 입력한 번호는 사라집니다. 계속하시겠습니까?"
    )) return;
    onChange(next);
  }

  function deleteSection(sectionNo) {
    if (!window.confirm(`Delete all questions in Section ${sectionNo}?`)) return;
    onChange(resequenceSections(rows.filter((r) => r.sectionNo !== sectionNo)));
  }

  // Deleting a 상위 문항 takes its 하위 문항 with it — they are written as sub-items of
  // this question and mean nothing without it — so the count goes in the prompt.
  function deleteRow(_uid) {
    const row = rows.find((r) => r._uid === _uid);
    if (!row) return;
    const label = row.questionText?.trim() || row.questionKey?.trim() || "이 질문";
    const block = blockOf(rows, row);
    const kids = block.length - 1;
    if (!window.confirm(kids
      ? `"${label}"과(와) 하위 문항 ${kids}개를 삭제하시겠습니까?`
      : `"${label}"을(를) 삭제하시겠습니까?`)) return;
    const settled = applyStructuralChange(rows.filter((r) => !block.includes(r)));
    if (settled) onChange(resequenceSections(settled));
  }

  // Renaming a key rewrites both things that point at a question by key: the options
  // that reveal it, and the 하위 문항 that hang off it.
  function handleKeyEditConfirm(sanitized) {
    const oldKey = keyEditTarget.currentKey;
    onChange(rows.map((r) => {
      let updated = r._uid === keyEditTarget._uid ? { ...r, questionKey: sanitized } : r;
      if (updated.parentKey === oldKey) updated = { ...updated, parentKey: sanitized };
      if (!updated.options?.length) return updated;
      return {
        ...updated,
        options: updated.options.map((opt) => ({
          ...opt,
          showsQuestions: (opt.showsQuestions || []).map((k) => (k === oldKey ? sanitized : k)),
        })),
      };
    }));
    setKeyEditTarget(null);
  }

  // A drag moves a question, and the slot it lands in decides its level.
  //
  // A 상위 문항 travels with its 하위 문항 as one block. Where the slot reads
  // unambiguously — inside another parent's run of children — the dragged question takes
  // that level. Where it reads either way (right after a block ends, or between two
  // 상위 문항) it keeps the level it already had, so dragging stays a way to reorder
  // 상위 문항 among themselves rather than silently nesting them. The → / ← buttons are
  // there for the times the level itself is what needs changing.
  function reorderRow(srcUid, dstUid, half) {
    if (!srcUid || !dstUid || srcUid === dstUid) return;
    const src = rows.find((r) => r._uid === srcUid);
    const dst = rows.find((r) => r._uid === dstUid);
    if (!src || !dst || src.sectionNo !== dst.sectionNo) return;

    const block = blockOf(rows, src);
    if (block.includes(dst)) return; // 자기 하위 문항 사이로는 옮길 수 없다

    const without = rows.filter((r) => !block.includes(r));
    let at = without.indexOf(dst) + (half === "bottom" ? 1 : 0);

    // A question that has 하위 문항 of its own can't become one, and must not be wedged
    // into someone else's block either — push the slot past the children it landed among.
    const hasKids = block.length > 1;
    if (hasKids) while (at < without.length && without[at].parentKey) at++;

    const above = without[at - 1];
    const below = without[at];
    const inSection = (r) => r && r.sectionNo === src.sectionNo;

    let parentKey = null;
    if (!hasKids) {
      if (inSection(below) && below.parentKey) parentKey = below.parentKey;
      else if (inSection(above)) {
        parentKey = isChild(src) ? (above.parentKey || above.questionKey) : null;
      }
    }

    const next = [...without];
    next.splice(at, 0, ...block.map((r, i) => (i === 0 ? { ...r, parentKey } : r)));
    const settled = applyStructuralChange(next);
    if (settled) onChange(settled);
  }

  const sections = groupBySec(rows);
  const colSpan = 5 + (editable ? 3 : 0) + (showActive ? 1 : 0);

  return (
    <div>
      {sections.map(({ no: secNo, rows: secRows }) => (
        <SectionGroup key={secNo}>
          <SectionHead>
            <SectionLabel>Section {secNo}:</SectionLabel>
            <SectionTitleInput
              readOnly={!editable}
              value={secRows[0]?.sectionTitle || ""}
              onChange={(e) => updateSectionTitle(secNo, e.target.value)}
              placeholder="Section title"
              maxLength={MAX_LEN.sectionTitle}
            />
            {editable && (
              <>
                <AddQBtn onClick={() => addQuestion(secNo)}>+ Question</AddQBtn>
                <DelSecBtn onClick={() => deleteSection(secNo)}>Delete Section</DelSecBtn>
              </>
            )}
          </SectionHead>

          <Table>
            <thead>
              <tr>
                {editable && <Th style={{ width: 28 }}></Th>}
                {editable && <Th style={{ width: 66 }} title="들여쓰기 / 내어쓰기 / 하위 문항 추가">단계</Th>}
                <Th style={{ width: 70 }}>No.</Th>
                <Th style={{ width: 160 }}>Key</Th>
                <Th>Question</Th>
                <Th style={{ width: 110 }}>Type</Th>
                <Th style={{ width: 50, textAlign: "center" }}>필수</Th>
                {showActive && <Th style={{ width: 60, textAlign: "center" }}>Active</Th>}
                {editable && <Th style={{ width: 40, textAlign: "center" }}>Delete</Th>}
              </tr>
            </thead>
            <tbody>
              {secRows.map((row, rowIdx) => {
                const showAbove = (dragOver.uid === row._uid && dragOver.half === "top") ||
                  (rowIdx > 0 && dragOver.uid === secRows[rowIdx - 1]._uid && dragOver.half === "bottom");
                const showBelow = rowIdx === secRows.length - 1 &&
                  dragOver.uid === row._uid && dragOver.half === "bottom";
                return (
                  <React.Fragment key={row._uid}>
                    <tr
                      onDragOver={editable ? (e) => {
                        e.preventDefault();
                        if (!dragSrcUid.current) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const half = e.clientY < rect.top + rect.height / 2 ? "top" : "bottom";
                        setDragOver({ uid: row._uid, half });
                      } : undefined}
                      onDrop={editable ? (e) => {
                        e.preventDefault();
                        reorderRow(dragSrcUid.current, dragOver.uid, dragOver.half);
                        setDragOver({ uid: null, half: null });
                      } : undefined}
                      onDragLeave={editable ? (e) => {
                        if (!e.currentTarget.contains(e.relatedTarget))
                          setDragOver((p) => (p.uid === row._uid ? { uid: null, half: null } : p));
                      } : undefined}
                      style={showAbove ? { boxShadow: "0 -2px 0 0 #0d6efd" } : showBelow ? { boxShadow: "0 2px 0 0 #0d6efd" } : {}}
                    >
                      {editable && (
                        <DragHandleTd
                          draggable
                          onDragStart={() => { dragSrcUid.current = row._uid; }}
                          onDragEnd={() => { dragSrcUid.current = null; setDragOver({ uid: null, half: null }); }}
                        >
                          ⠿
                        </DragHandleTd>
                      )}
                      {editable && (
                        <LevelCellTd>
                          <LevelBtn
                            type="button"
                            disabled={!indentTargetOf(row)}
                            onClick={() => indentRow(row)}
                            title={
                              isChild(row) ? "이미 하위 문항입니다."
                              : childrenOf(rows, row).length
                                ? "하위 문항이 있는 문항은 내릴 수 없습니다. 문항은 2단계까지만 만들 수 있습니다."
                                : indentTargetOf(row)
                                  ? "바로 위 상위 문항의 하위 문항으로 만듭니다."
                                  : "위에 상위 문항이 없어 내릴 수 없습니다."
                            }
                          >
                            →
                          </LevelBtn>
                          <LevelBtn
                            type="button"
                            disabled={!isChild(row)}
                            onClick={() => outdentRow(row)}
                            title={isChild(row)
                              ? "상위 문항으로 올립니다."
                              : "이미 상위 문항입니다."}
                            style={{ marginLeft: 2 }}
                          >
                            ←
                          </LevelBtn>
                          <LevelBtn
                            type="button"
                            disabled={isChild(row)}
                            onClick={() => addSubQuestion(row)}
                            title={isChild(row)
                              ? "하위 문항 아래에는 문항을 더 만들 수 없습니다. 문항은 2단계까지입니다."
                              : "이 문항의 하위 문항을 추가합니다."}
                            style={{ marginLeft: 2 }}
                          >
                            +
                          </LevelBtn>
                        </LevelCellTd>
                      )}
                      <IndentTd $child={isChild(row)}>
                        {editable ? (
                          <InlineInput
                            type="text"
                            value={row.questionNo || ""}
                            onChange={(e) => updateRow(row._uid, "questionNo", e.target.value)}
                            maxLength={MAX_LEN.questionNo}
                            title={dupeNoUids.has(row._uid)
                              ? (row.parentKey
                                  ? "이 번호가 같은 상위 문항의 다른 하위 문항과 겹칩니다."
                                  : "이 번호가 같은 섹션의 다른 문항과 겹칩니다.")
                              : undefined}
                            style={{ width: 36,
                                     borderColor: dupeNoUids.has(row._uid) ? "#dc3545" : undefined }}
                          />
                        ) : (
                          branchNo(row)
                        )}
                      </IndentTd>
                      <Td>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                          <code style={{ fontSize: 11, color: "#495057", background: "#f1f3f5", padding: "2px 6px", borderRadius: 3, whiteSpace: "nowrap" }}>
                            {row.questionKey}
                          </code>
                          {editable && (
                            <button
                              type="button"
                              onClick={() => setKeyEditTarget({ _uid: row._uid, currentKey: row.questionKey || "" })}
                              style={{ fontSize: 11, padding: "1px 6px", border: "1px solid #ced4da", borderRadius: 3, background: "transparent", cursor: "pointer", flexShrink: 0 }}
                            >
                              수정
                            </button>
                          )}
                        </div>
                      </Td>
                      <Td>
                        {editable ? (
                          <>
                            <InlineInput
                              ref={(el) => { if (rowRefs) rowRefs.current[`${row._uid}_questionText`] = el; }}
                              type="text"
                              value={row.questionText || ""}
                              onChange={(e) => updateRow(row._uid, "questionText", e.target.value)}
                              placeholder="Question text"
                              maxLength={MAX_LEN.questionText}
                            />
                            {YN_TYPES.includes(row.questionType) && (
                              <SpecifyRow>
                                <input
                                  type="checkbox"
                                  id={`sp_${row._uid}`}
                                  checked={!!row.hasSpecify}
                                  onChange={(e) => updateRow(row._uid, "hasSpecify", e.target.checked)}
                                />
                                <label htmlFor={`sp_${row._uid}`} style={{ fontSize: 12, color: "#6c757d", cursor: "pointer" }}>
                                  Specify field
                                </label>
                                {row.hasSpecify && (
                                  <>
                                    {YN_CODES[row.questionType].map((code) => (
                                      <DisablesBtn
                                        key={code}
                                        type="button"
                                        $active={parseSpecifyWhen(row.specifyWhen).includes(code)}
                                        onClick={() => toggleSpecifyWhen(row, code)}
                                        title="이 답변을 선택했을 때만 입력칸을 표시 (아무것도 선택하지 않으면 항상 표시)"
                                      >
                                        {YN_LABEL[code]}
                                      </DisablesBtn>
                                    ))}
                                    {!parseSpecifyWhen(row.specifyWhen).length && (
                                      <SpecifyHint>항상 표시</SpecifyHint>
                                    )}
                                    <InlineInput
                                      type="text"
                                      value={row.specifyLabel || ""}
                                      onChange={(e) => updateRow(row._uid, "specifyLabel", e.target.value)}
                                      placeholder="Label (e.g. 'If yes, describe:')"
                                      style={{ flex: 1, minWidth: 120 }}
                                      maxLength={MAX_LEN.specifyLabel}
                                    />
                                  </>
                                )}
                              </SpecifyRow>
                            )}
                          </>
                        ) : (
                          <>
                            {row.questionText}
                            <SpecifyDetail
                              type={row.questionType}
                              hasSpecify={row.hasSpecify}
                              specifyLabel={row.specifyLabel}
                              specifyWhen={row.specifyWhen}
                            />
                            {row.questionType === "checklist" && (
                              <ChecklistDetail
                                options={row.options}
                                singleSelect={row.singleSelect}
                                resolveQuestion={resolveQuestion}
                              />
                            )}
                          </>
                        )}
                      </Td>
                      <Td>
                        {editable ? (
                          <TypeSelect
                            value={row.questionType}
                            onChange={(e) => changeQuestionType(row._uid, e.target.value)}
                          >
                            {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </TypeSelect>
                        ) : (
                          QUESTION_TYPE_LABELS[row.questionType] || row.questionType
                        )}
                      </Td>
                      <Td style={{ textAlign: "center" }}>
                        {editable ? (
                          <input
                            type="checkbox"
                            checked={!!row.isRequired}
                            onChange={(e) => updateRow(row._uid, "isRequired", e.target.checked)}
                            title="응답자가 반드시 답해야 하는 질문"
                          />
                        ) : (
                          <span style={{ color: row.isRequired ? "#dc3545" : "#adb5bd" }}>
                            {row.isRequired ? "*" : "–"}
                          </span>
                        )}
                      </Td>
                      {showActive && (
                        <Td style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={row.isActive !== false}
                            disabled={!editable}
                            onChange={(e) => updateRow(row._uid, "isActive", e.target.checked)}
                          />
                        </Td>
                      )}
                      {editable && (
                        <Td style={{ textAlign: "center" }}>
                          <DelRowBtn onClick={() => deleteRow(row._uid)}>✕</DelRowBtn>
                        </Td>
                      )}
                    </tr>
                    {editable && row.questionType === "checklist" && (
                      <tr>
                        <OptionsPanelTd colSpan={colSpan}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#495057", cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={!!row.singleSelect}
                                onChange={(e) => updateRow(row._uid, "singleSelect", e.target.checked)}
                              />
                              Single select (radio behavior)
                            </label>
                          </div>
                          <OptionsEditor
                            options={row.options || []}
                            onChange={(opts) => updateRow(row._uid, "options", opts)}
                            rowRefs={rowRefs}
                            rowUid={row._uid}
                            showQTargets={showQCandidates(rows, row).map((r) => ({
                              key:          r.questionKey,
                              no:           branchNo(r),
                              text:         r.questionText,
                              sectionNo:    r.sectionNo,
                              sectionTitle: r.sectionTitle,
                            }))}
                          />
                        </OptionsPanelTd>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </SectionGroup>
      ))}

      {editable && (
        <BottomBar>
          <AddSecBtn type="button" onClick={addSection}>+ Add Section</AddSecBtn>
          <RenumberBtn
            type="button"
            disabled={!rows.length}
            onClick={renumberQuestions}
            title="각 섹션의 No.를 표시 순서대로 1, 2, 3… 으로 다시 매깁니다."
          >
            No. 순서대로 다시 매기기
          </RenumberBtn>
        </BottomBar>
      )}

      {keyEditTarget && (
        <KeyEditModal
          target={keyEditTarget}
          rows={rows}
          onConfirm={handleKeyEditConfirm}
          onClose={() => setKeyEditTarget(null)}
        />
      )}
    </div>
  );
}
